import { Box, Container, Flex, Text, Heading, Grid } from '@radix-ui/themes';
import { Image } from '@trashlab/sitesapp/src/organizations/best-deal/components/shared/image';
import { useTenantContext } from '@trashlab/sitesapp/src/organizations/best-deal/contexts/use-tenant-context';
import { Button } from '@trashlab/sitesapp/src/organizations/best-deal/template/components/shared/button';
import { IconCheckmark } from '@trashlab/sitesapp/src/organizations/best-deal/template/components/icons/icons';
import { useVariantValue } from '@trashlab/sitesapp/src/organizations/best-deal/lib/utils/get-variant-value';
import { useThemeTextColor } from '@trashlab/sitesapp/src/organizations/best-deal/lib/utils/use-theme-text-color';
import { asAddress } from '@trashlab/api/src/library/server/utils/json-compatibility';

export const WhyChooseUs = () => {
  const { website } = useTenantContext();
  const { textColorClass, isLightBackground } = useThemeTextColor();
  const orgAddress = asAddress(website?.organization?.address as any);
  const city = orgAddress?.city ?? 'San Francisco';
  const state = orgAddress?.state ?? 'CA';
  
  const whyChooseUsSectionTitle = useVariantValue(
    'WHY_CHOOSE_US_SECTION_TITLE',
    `Why work with ${website?.organization?.name || 'our company'}`,
  );
  
  const whyChooseUsImage = useVariantValue(
    'WHY_CHOOSE_US_IMAGE',
    '/default/why-choose-us.svg',
  );

  const whyChooseUsTitle = useVariantValue(
    'WHY_CHOOSE_US_TITLE',
    `Best dumpster rental service in ${city}`,
  );

  const whyChooseUsSubtitle = useVariantValue(
    'WHY_CHOOSE_US_SUBTITLE',
    `At ${website?.organization?.name || 'our company'}, we
    understand that renting a dumpster in ${city} is a big decision. Our
    goal is to make your dumpster rental experience seamless and
    convenient. We offer flexible rental durations to fit your needs,
    and our friendly customer service team is here to guide you through
    the process. With competitive rates and reliable service,
    ${website?.organization?.name || 'our company'} is your top
    choice for stress-free waste removal and junk disposal in ${city}.`,
  );

  const whyChooseUsFeatures = useVariantValue('WHY_CHOOSE_US_FEATURE', [
    {
      value: 'Short term & long term rentals',
      orderIndex: 0,
    },
    {
      value: 'Safe reliable delivery',
      orderIndex: 1,
    },
    {
      value: `${
        website?.organization?.name || "Our company's"
      } rental delivery available`,
      orderIndex: 2,
    },
    {
      value: 'Quick and easy ordering',
      orderIndex: 3,
    },
  ]);

  const aboutCompanyButtonText = useVariantValue(
    'ABOUT_COMPANY_BUTTON_TEXT',
    'About our company',
  );

  return (
    <Container size="4" p="8">
      <Grid
        columns={{ initial: '1', xs: '2' }}
        width="auto"
        justify="start"
        align="start"
      >
        <Flex direction="column" gap="4" className="h-full flex justify-center">
          <Text size="2" weight="bold" className="text-neutral-400" data-variable="WHY_CHOOSE_US_SECTION_TITLE">
            {whyChooseUsSectionTitle}
          </Text>

          <Heading as="h2" size="8" data-variable="WHY_CHOOSE_US_TITLE">
            {whyChooseUsTitle}
          </Heading>

          <Text
            size="3"
            data-variable="WHY_CHOOSE_US_SUBTITLE"
          >
            {whyChooseUsSubtitle}
          </Text>


          <Flex direction="column" gap="3">
            {Array.isArray(whyChooseUsFeatures) &&
              [...whyChooseUsFeatures]
                .sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0))
                .map((feature) => (
                  <Flex
                    key={`${feature.orderIndex}-${feature.value}`}
                    align="center"
                    gap="2"
                    data-variable="WHY_CHOOSE_US_FEATURE"
                    data-order-index={feature.orderIndex}
                  >
                    <IconCheckmark
                      className="w-6 h-6"
                    />
                    <Text size={{ initial: '3', md: '4' }} weight="bold">
                      {feature.value}
                    </Text>
                  </Flex>
                ))}
          </Flex>

          <Box className="flex items-center col-span-1 my-4">
            <Button
              href="/about"
              withArrow
              title="Learn about our dumpster rental company"
              aria-label="Learn more about our company's history, values, and dumpster rental services"
              data-variable="ABOUT_COMPANY_BUTTON_TEXT"
            >
              {aboutCompanyButtonText}
            </Button>
          </Box>
        </Flex>

        <Box
          className="flex justify-center items-center"
          data-variable="WHY_CHOOSE_US_IMAGE"
        >
          <Image
            src={whyChooseUsImage}
            alt="Why choose us"
            className="rounded-lg"
          />
        </Box>
      </Grid>
    </Container>
  );
};

export default WhyChooseUs;
